import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';

type LoadingOverlayProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  // Optional escape hatch: when provided, renders a Cancel button and lets Escape dismiss
  // the overlay. The underlying request (e.g. via axios timeout, or aborting the RTK Query
  // mutation) is not affected — the caller decides what "cancel" means for its own request.
  onCancel?: () => void;
};

// Full-screen blocking overlay used while a long-running action (e.g. an export)
// is in progress, so the user can't navigate away or interact with the page until it settles.
const LoadingOverlay = ({ open, title, subtitle, onCancel }: LoadingOverlayProps) => {
  useEffect(() => {
    if (!open || !onCancel) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-200 flex items-center justify-center bg-black/45 backdrop-blur-[2px]"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={title}
    >
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-violet/10 bg-white px-10 py-8">
        <Loader2 className="size-10 animate-spin text-[var(--m4-purple-700)]" aria-hidden />
        <p className="font-heading text-base font-semibold text-fg-1">{title}</p>
        {subtitle && <p className="text-sm text-fg-3">{subtitle}</p>}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-1 text-sm font-medium text-fg-3 underline-offset-2 hover:text-fg-1 hover:underline"
          >
            Cancel
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LoadingOverlay;
